import * as React from 'react';

import {
  CurrencyCode,
  enumCurrencyCode,
  Unit,
} from '@rematter/frontend-lib-api-client';
import { cn, MeasuredValue, Money as MoneyDisplay } from '@rematter/ui-web';

import {
  DescriptionFieldCell,
  QuantityFieldCell,
  UnitFieldCell,
  UnitPriceFieldCell,
} from './fields';
import { FinanceLineItemData, FinanceLineItemErrors } from './types';

import { useTranslations } from '@/finance/i18n/useTranslations';
import {
  DeleteButtonCell,
  FooterCell,
  HeaderCell,
  isSimpleGroup,
  LineItemFooter,
  LineItemHeader,
  LineItemRow,
  LineItemTable,
  PriceFieldCell,
} from '~/shared/components/line-items';

const GRID_TEMPLATE =
  'grid-cols-[minmax(16rem,2fr)_1fr_80px_1fr_1fr_var(--size-input)]';

const GRID_TEMPLATE_NO_ACTIONS =
  'grid-cols-[minmax(16rem,2fr)_1fr_80px_1fr_1fr]';

type LineItemGroup = {
  groupName: string | null;
  items: FinanceLineItemData[];
};

export interface FinanceLineItemTableProps {
  lineItems: FinanceLineItemData[];
  currencyCode?: CurrencyCode;
  onUpdateLineItem: (id: string, updates: Partial<FinanceLineItemData>) => void;
  onDeleteLineItem: (id: string) => void;
  onAddLineItem?: () => void;
  disabled?: boolean;
  hideAddButton?: boolean;
  hideActions?: boolean;
  isLoading?: boolean;
  errors?: Record<string, FinanceLineItemErrors>;
}

//
// ⚛️ ⚛️ ⚛️ FINANCE LINE ITEM TABLE ⚛️ ⚛️ ⚛️
//
export const FinanceLineItemTable: React.FC<FinanceLineItemTableProps> = ({
  lineItems,
  currencyCode = enumCurrencyCode.USD,
  onUpdateLineItem,
  onDeleteLineItem,
  onAddLineItem,
  disabled = false,
  hideAddButton = false,
  hideActions = false,
  isLoading = false,
  errors,
}) => {
  // Translations
  const t = useTranslations();

  // Memoized values
  const isEmpty = lineItems.length === 0;

  const groupedItems = React.useMemo(() => {
    const groups: LineItemGroup[] = [];
    let currentGroup: LineItemGroup | null = null;

    for (const item of lineItems) {
      if (!currentGroup || currentGroup.groupName !== item.groupName) {
        currentGroup = { groupName: item.groupName, items: [] };
        groups.push(currentGroup);
      }
      currentGroup.items.push(item);
    }

    return groups;
  }, [lineItems]);

  const quantityByUnit = React.useMemo(() => {
    const groups = new Map<string | null, number>();
    for (const item of lineItems) {
      if (item.quantity && !isSimpleGroup(item.groupName)) {
        const unit = item.unit;
        groups.set(unit, (groups.get(unit) ?? 0) + item.quantity);
      }
    }
    return Array.from(groups.entries());
  }, [lineItems]);

  const totalPrice = React.useMemo(
    () =>
      lineItems.reduce((sum, item) => sum + (item.totalPrice?.amount ?? 0), 0),
    [lineItems],
  );

  const gridTemplate = hideActions ? GRID_TEMPLATE_NO_ACTIONS : GRID_TEMPLATE;

  return (
    <LineItemTable
      isEmpty={isEmpty}
      onAdd={onAddLineItem}
      hideAddButton={hideAddButton}
      disabled={disabled}
      isLoading={isLoading}
      addButtonLabel={t.lineItems.addButton}
      className="@FinanceLineItemTable"
    >
      <LineItemHeader gridTemplate={gridTemplate}>
        <HeaderCell className="justify-start">
          {t.lineItems.columns.description}
        </HeaderCell>
        <HeaderCell>{t.lineItems.columns.quantity}</HeaderCell>
        <HeaderCell>{t.lineItems.columns.unit}</HeaderCell>
        <HeaderCell>{t.lineItems.columns.unitPrice}</HeaderCell>
        <HeaderCell>{t.lineItems.columns.totalPrice}</HeaderCell>
        {!hideActions && <HeaderCell />}
      </LineItemHeader>

      {groupedItems.map((group, groupIndex) => (
        <React.Fragment key={group.groupName ?? '__ungrouped__'}>
          {group.groupName != null && (
            <div
              className={cn(
                '@GroupHeader text-sm font-semibold text-neutral',
                groupIndex > 0 && 'pt-3',
              )}
            >
              {group.groupName}
            </div>
          )}
          {group.items.map((lineItem) => {
            const simple = isSimpleGroup(group.groupName);
            return (
              <LineItemRow key={lineItem.id} gridTemplate={gridTemplate}>
                <DescriptionFieldCell
                  lineItem={lineItem}
                  onUpdate={onUpdateLineItem}
                  disabled={disabled}
                  errors={errors?.[lineItem.id]}
                />
                {simple ? (
                  <>
                    <div />
                    <div />
                  </>
                ) : (
                  <>
                    <QuantityFieldCell
                      lineItem={lineItem}
                      currencyCode={currencyCode}
                      onUpdate={onUpdateLineItem}
                      disabled={disabled}
                      errors={errors?.[lineItem.id]}
                    />
                    <UnitFieldCell
                      lineItem={lineItem}
                      onUpdate={onUpdateLineItem}
                      disabled={disabled}
                      errors={errors?.[lineItem.id]}
                    />
                  </>
                )}
                <UnitPriceFieldCell
                  lineItem={lineItem}
                  currencyCode={currencyCode}
                  onUpdate={onUpdateLineItem}
                  disabled={disabled}
                  errors={errors?.[lineItem.id]}
                  allowNegative={simple}
                />
                <PriceFieldCell
                  value={lineItem.totalPrice?.amount ?? 0}
                  disabled
                  minValue={simple ? Number.MIN_SAFE_INTEGER : 0}
                />
                {!hideActions && (
                  <DeleteButtonCell
                    onDelete={() => onDeleteLineItem(lineItem.id)}
                    disabled={disabled}
                  />
                )}
              </LineItemRow>
            );
          })}
        </React.Fragment>
      ))}

      {!isEmpty && (
        <LineItemFooter gridTemplate={gridTemplate}>
          <FooterCell className="font-bold text-neutral">
            {t.lineItems.footer.items(lineItems.length)}
          </FooterCell>
          <FooterCell className="flex-col items-end gap-1 font-bold">
            {quantityByUnit.map(([unit, total]) => (
              <div key={unit ?? '__none__'}>
                <div>
                  <MeasuredValue value={total} unit={unit as Unit} />
                </div>
              </div>
            ))}
          </FooterCell>
          <FooterCell />
          <FooterCell />
          <FooterCell className="justify-end font-bold">
            <MoneyDisplay amount={totalPrice} />
          </FooterCell>
          {!hideActions && <FooterCell />}
        </LineItemFooter>
      )}
    </LineItemTable>
  );
};
